/* eslint-disable no-param-reassign */
import { createSlice, current } from '@reduxjs/toolkit';

const initialState = {
  fields: [],
  selected: false,
};

const newKey = (key) => {
  const match = key.match(/^(\w+)-([0-9])$/);
  if (!match) return `${key}-2`;
  return `${match[1]}-${parseInt(match[2], 10) + 1}`;
};

const generateKey = (fields, key) => {
  const exists = fields.find((f) => f.key === key);
  if (exists) return generateKey(fields, newKey(key));
  return key;
};

const fieldTemplates = {
  Input: {
    type: 'Input',
    props: { label: { value: 'New Item' } },
  },
  Textarea: {
    type: 'Textarea',
    props: { label: { value: 'New Textarea' } },
  },
  Select: {
    type: 'Select',
    props: {
      label: { value: 'New Select' },
      options: [],
    },
  },
  Text: {
    type: 'Text',
    props: {
      value: 'Type here your text',
      tag: 'h1',
      fontSize: 'xl',
    },
  },
  Checkbox: {
    type: 'Checkbox',
    props: {
      label: { value: 'New Checkbox' },
      allowIndeterminate: false,
    },
  },
  Radio: {
    type: 'Radio',
    props: {
      direction: 'column',
      options: [{ key: 'first', value: 'First' }, { key: 'second', value: 'Second' }],
    },
  },
  DatePicker: {
    type: 'DatePicker',
    props: {
      label: { value: 'Date' },
    },
  },
};

const editorSlice = createSlice({
  name: 'editor',
  initialState,
  reducers: {
    setFields(state, action) {
      state.fields = action.payload;
    },
    setField(state, action) {
      const idx = state.fields.findIndex((f) => f.key === action.payload.key);
      const newItem = { ...action.payload.field };
      if (action.payload.key !== newItem.key) newItem.key = generateKey(state.fields, action.payload.field.key);
      state.fields[idx] = newItem;
      if (state.selected === action.payload.key) {
        state.selected = newItem.key;
      }
    },
    setSelected(state, action) {
      state.selected = action.payload;
    },
    removeField(state, action) {
      state.fields.splice(action.payload, 1);
    },
    moveUp(state, action) {
      const index = action.payload;
      if (index > 0) {
        const temp = state.fields[index - 1];
        state.fields[index - 1] = state.fields[index];
        state.fields[index] = temp;
      }
    },
    moveDown(state, action) {
      const index = action.payload;
      if (index < state.fields.length - 1) {
        const temp = state.fields[index + 1];
        state.fields[index + 1] = state.fields[index];
        state.fields[index] = temp;
      }
    },
    swap(state, action) {
      const { id1, id2 } = action.payload;
      const temp = state.fields[id1];
      state.fields[id1] = state.fields[id2];
      state.fields[id2] = temp;
    },
    moveTo(state, action) {
      const { from, to } = action.payload;
      const temp = state.fields[from];
      state.fields.splice(from, 1);
      if (to > from) {
        state.fields.splice(to - 1, 0, temp);
      } else {
        state.fields.splice(to, 0, temp);
      }
    },
    cloneField(state, action) {
      const newItem = { ...current(state.fields[action.payload]) };
      const key = generateKey(state.fields, newItem.key);
      newItem.key = key;
      state.fields.splice(action.payload + 1, 0, newItem);
      state.selected = key;
    },
    insertInto(state, action) {
      const item = { ...fieldTemplates[action.payload.type] };
      item.key = generateKey(state.fields, action.payload.type.toLowerCase());
      state.fields.splice(action.payload.pos, 0, item);
      state.selected = item.key;
    },
  },
});

export const {
  setFields, setSelected, setField, removeField, moveUp, moveDown, cloneField, swap, moveTo, insertInto,
} = editorSlice.actions;

export default editorSlice.reducer;

export const getFields = (state) => state.editor.fields;

export const isSelected = (state, key) => state.editor.selected && state.editor.selected === key;

export const getSelected = (state) => state.editor.fields.find((f) => f.key === state.editor.selected);
